import { useEffect, useState, useRef, useCallback } from 'react';
import mqtt from 'mqtt';

/**
 * Hook for subscribing to an AI agent's MQTT topics and managing thought state.
 * 
 * Supports two publish patterns:
 * 
 * 1. Subtopic pattern — publish to {topicBase}/status and {topicBase}/thought separately
 *    with plain-text values (e.g., "thinking", "running records").
 * 
 * 2. Combined JSON pattern — publish a single JSON payload to {topicBase} with
 *    "status" and/or "thought"/"thoughts" fields:
 *    { "status": "thinking", "thought": "running records" }
 * 
 * @param {Object} mqttConfig - MQTT connection configuration
 * @param {string} mqttConfig.broker - WebSocket URL of the MQTT broker
 * @param {string} mqttConfig.username - MQTT username
 * @param {string} mqttConfig.password - MQTT password
 * @param {string} topicBase - Base topic path for the agent (e.g., "agents/shift-review")
 * @returns {Object} - { thoughts: string[], status: 'idle'|'thinking'|'done', isHighlighted: boolean, isConnected: boolean }
 */
const useAgentMqtt = (mqttConfig, topicBase) => {
  const [thoughts, setThoughts] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle', 'thinking', 'done'
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  
  const clientRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const isCleaningUpRef = useRef(false);
  const statusRef = useRef('idle');
  
  const HIGHLIGHT_DURATION_MS = 3000;

  // Handle status message
  const handleStatusMessage = useCallback((statusValue) => {
    const normalizedStatus = statusValue.toLowerCase().trim();
    
    if (normalizedStatus === 'thinking') {
      // Only clear thoughts on TRANSITION to thinking (not on repeated "Thinking" messages)
      const wasThinking = statusRef.current === 'thinking';
      if (!wasThinking) {
        // First "Thinking" in a new cycle — clear previous thoughts
        setThoughts([]);
      }
      statusRef.current = 'thinking';
      setStatus('thinking');
      setIsHighlighted(false);
      
      // Clear any existing highlight timeout
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    } else if (normalizedStatus === 'done' || normalizedStatus === 'idle') {
      setStatus((prevStatus) => {
        // Only trigger completion effect if we were thinking
        if (prevStatus === 'thinking') {
          setIsHighlighted(true);
          
          // Remove highlight and reset to idle after duration
          highlightTimeoutRef.current = setTimeout(() => {
            setIsHighlighted(false);
            setStatus('idle');
            statusRef.current = 'idle';
            // Clear thoughts when transitioning to idle after completion
            setThoughts([]);
          }, HIGHLIGHT_DURATION_MS);
          
          statusRef.current = 'done';
          return 'done';
        }
        
        // If explicitly set to 'idle', transition to idle
        if (normalizedStatus === 'idle') {
          statusRef.current = 'idle';
          // Clear thoughts when going idle
          setThoughts([]);
          return 'idle';
        }
        
        // If 'done' and already done, stay done
        return prevStatus;
      });
    }
  }, []);

  // Handle thought message
  const handleThoughtMessage = useCallback((thoughtValue) => {
    if (!thoughtValue) return;
    
    // Add thought to the list
    setThoughts(prev => [...prev, thoughtValue]);
  }, []);

  useEffect(() => {
    // Don't connect if no config or no topic base
    if (!mqttConfig?.broker || !topicBase) {
      return;
    }

    // Create a local flag for this specific effect instance (avoids race conditions with refs)
    let isCleanedUp = false;
    
    // Reset cleanup flag for this effect instance
    isCleaningUpRef.current = false;

    const wildcardTopic = `${topicBase}/+`;
    const baseTopic = topicBase;
    
    const clientId = `vtec_agent_${topicBase.replace(/\//g, '_')}_${Date.now()}`;

    const client = mqtt.connect(mqttConfig.broker, {
      username: mqttConfig.username,
      password: mqttConfig.password,
      rejectUnauthorized: false,
      connectTimeout: 30 * 1000,
      reconnectPeriod: 5 * 1000,
      keepalive: 60,
      clean: true,
      clientId: clientId,
      protocolVersion: 4,
    });
    
    clientRef.current = client;
    
    // Reset state on new connection
    setIsConnected(false);
    
    // Connection timeout
    const connectionTimeout = setTimeout(() => {
      console.error(`Agent MQTT connection timeout for ${topicBase}`);
    }, 30000);
    
    // On connection
    client.on('connect', () => {
      // Don't proceed if already cleaned up
      if (isCleanedUp) {
        client.end(true);
        return;
      }
      
      clearTimeout(connectionTimeout);
      setIsConnected(true);
      
      client.subscribe([wildcardTopic, baseTopic], (err) => {
        if (err && !isCleanedUp) {
          console.error(`Failed to subscribe to ${wildcardTopic} / ${baseTopic}:`, err);
        }
      });
    });
    
    // On reconnect
    client.on('reconnect', () => {
      console.log(`Agent MQTT reconnecting for ${topicBase}...`);
    });
    
    // On disconnect
    client.on('disconnect', () => {
      setIsConnected(false);
    });
    
    // On offline
    client.on('offline', () => {
      setIsConnected(false);
    });
    
    client.on('message', (topic, message) => {
      const value = message.toString().trim();

      if (topic === baseTopic) {
        // Combined JSON on the base topic
        let parsed;
        try { parsed = JSON.parse(value); } catch { return; }
        if (!parsed || typeof parsed !== 'object') return;

        if (parsed.status) handleStatusMessage(String(parsed.status));

        const thought = parsed.thought ?? parsed.thoughts;
        if (thought) handleThoughtMessage(String(thought));
      } else {
        // Subtopic pattern: {topicBase}/status or {topicBase}/thought
        const subtopic = topic.split('/').pop().toLowerCase();
        if (subtopic === 'status') {
          handleStatusMessage(value);
        } else if (subtopic === 'thought' || subtopic === 'thoughts') {
          handleThoughtMessage(value);
        }
      }
    });
    
    // Handle connection errors
    client.on('error', (err) => {
      console.error(`Agent MQTT error for ${topicBase}:`, err);
    });
    
    // Cleanup on unmount or config change
    return () => {
      // Mark that we're intentionally cleaning up (suppresses "client disconnecting" errors)
      isCleanedUp = true;
      isCleaningUpRef.current = true;
      
      clearTimeout(connectionTimeout);
      
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
      
      if (clientRef.current) {
        // Force disconnect without waiting for unsubscribe callbacks
        clientRef.current.end(true);
        clientRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mqttConfig?.broker, mqttConfig?.username, mqttConfig?.password, topicBase]);

  return { 
    thoughts, 
    status, 
    isHighlighted, 
    isConnected 
  };
};

export default useAgentMqtt;

